import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  Tabs,
  Tab,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Chip,
  Drawer,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Switch,
  FormControlLabel,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  Grid,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import {
  RefreshCw,
  Eye,
  Shield,
  CheckCircle,
  XCircle,
  Clock,
  Activity,
  Terminal,
  Layers,
  Globe,
  Settings,
  X,
} from 'lucide-react';
import {
  useToolCatalog,
  useToolCalls,
  useToolCallStats,
  useExternalDefinitions,
  useToolPolicies,
  useUpdateToolGlobalStatus,
  useUpdateExternalDefinitionStatus,
  useUpdateDeleteMethodPolicy,
  ToolCallLogItem,
  ExternalDefinitionItem,
  ToolCatalogItem,
} from '../../../services/tool-management.service';
import {
  usePackageTools,
  usePackages,
  useUpdatePackageTools,
} from '../../../services/package.service';
import { PackageToolAssignment } from '../../../types/admin';
import { useNotification } from '../../../hooks/useNotification';

export const ToolCallsManagementPage: React.FC = () => {
  const [currentTab, setCurrentTab] = useState(0);
  const notification = useNotification();

  // Tab 0: Calls state
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(20);
  const [toolIdFilter, setToolIdFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');
  const [riskFilter, setRiskFilter] = useState('');
  const [successFilter, setSuccessFilter] = useState('');
  const [requestIdFilter, setRequestIdFilter] = useState('');
  const [selectedCall, setSelectedCall] = useState<ToolCallLogItem | null>(null);

  // Queries
  const {
    data: callsData,
    isLoading: isCallsLoading,
    refetch: refetchCalls,
  } = useToolCalls({
    page: page + 1,
    limit: pageSize,
    toolId: toolIdFilter || undefined,
    source: sourceFilter || undefined,
    riskLevel: riskFilter || undefined,
    success: successFilter || undefined,
    requestId: requestIdFilter.trim() || undefined,
  });

  const { data: statsData, isLoading: isStatsLoading } = useToolCallStats();
  const { data: catalogData = [], isLoading: isCatalogLoading, refetch: refetchCatalog } = useToolCatalog();
  const updateGlobalStatusMutation = useUpdateToolGlobalStatus();

  const { data: packages = [] } = usePackages();
  const [selectedPackageId, setSelectedPackageId] = useState('');
  const {
    data: packageTools = [],
    isLoading: isPackageToolsLoading,
    refetch: refetchPackageTools,
  } = usePackageTools(selectedPackageId);
  const updatePackageToolsMutation = useUpdatePackageTools();

  // Tab 2: External definitions
  const [extPage, setExtPage] = useState(0);
  const [extPageSize, setExtPageSize] = useState(20);
  const [extSearch, setExtSearch] = useState('');
  const [statusDialogItem, setStatusDialogItem] = useState<ExternalDefinitionItem | null>(null);
  const [targetStatus, setTargetStatus] = useState('active');
  const [statusReason, setStatusReason] = useState('');

  const {
    data: extData,
    isLoading: isExtLoading,
    refetch: refetchExt,
  } = useExternalDefinitions({
    page: extPage + 1,
    limit: extPageSize,
    search: extSearch || undefined,
  });

  const updateStatusMutation = useUpdateExternalDefinitionStatus();

  // Tab 3: Policies
  const { data: policiesData, isLoading: isPoliciesLoading, refetch: refetchPolicies } = useToolPolicies();
  const [policyConfirmOpen, setPolicyConfirmOpen] = useState(false);
  const [pendingDeleteMethod, setPendingDeleteMethod] = useState(false);
  const [policyReason, setPolicyReason] = useState('');
  const updatePolicyMutation = useUpdateDeleteMethodPolicy();

  useEffect(() => {
    if (!selectedPackageId && packages.length > 0) {
      setSelectedPackageId(packages[0].publicId);
    }
  }, [packages, selectedPackageId]);

  // Handlers
  const handleOpenStatusDialog = (item: ExternalDefinitionItem) => {
    setStatusDialogItem(item);
    setTargetStatus(item.status === 'active' ? 'disabled' : 'active');
    setStatusReason('');
  };

  const handleConfirmStatusChange = async () => {
    if (!statusDialogItem || !statusReason.trim()) return;
    try {
      await updateStatusMutation.mutateAsync({
        publicId: statusDialogItem.publicId,
        payload: { status: targetStatus, reason: statusReason.trim() },
      });
      notification.success(`Tool status updated to ${targetStatus}`);
      setStatusDialogItem(null);
      refetchExt();
    } catch (err: any) {
      notification.error(err?.response?.data?.message || 'Failed to update tool status');
    }
  };

  const handleToggleDeleteMethod = (checked: boolean) => {
    setPendingDeleteMethod(checked);
    setPolicyReason('');
    setPolicyConfirmOpen(true);
  };

  const handleConfirmPolicyChange = async () => {
    if (!policyReason.trim()) return;
    try {
      await updatePolicyMutation.mutateAsync({
        enabled: pendingDeleteMethod,
        reason: policyReason.trim(),
      });
      notification.success(`Policy updated: DELETE method is now ${pendingDeleteMethod ? 'enabled' : 'disabled'}`);
      setPolicyConfirmOpen(false);
      refetchPolicies();
    } catch (err: any) {
      notification.error(err?.response?.data?.message || 'Failed to update policy');
    }
  };

  const handleToggleGlobalTool = async (tool: ToolCatalogItem, enabled: boolean) => {
    try {
      await updateGlobalStatusMutation.mutateAsync({
        toolId: tool.toolId,
        payload: {
          enabled,
          reason: `Admin ${enabled ? 'enabled' : 'disabled'} ${tool.toolId} globally from Tool Management`,
        },
      });
      notification.success(`${tool.toolId} is now ${enabled ? 'enabled' : 'disabled'} globally`);
      refetchCatalog();
      refetchPackageTools();
    } catch (err: any) {
      notification.error(err?.response?.data?.message || 'Failed to update global tool status');
    }
  };

  const handleTogglePackageTool = async (tool: PackageToolAssignment, enabled: boolean) => {
    if (!selectedPackageId) return;
    try {
      const nextTools = packageTools.map((item) => ({
        toolId: item.toolId,
        isEnabled: item.toolId === tool.toolId ? enabled : item.isEnabled,
      }));
      await updatePackageToolsMutation.mutateAsync({
        publicId: selectedPackageId,
        tools: nextTools,
      });
      notification.success(`${tool.toolId} is now ${enabled ? 'enabled' : 'disabled'} for package`);
      refetchPackageTools();
    } catch (err: any) {
      notification.error(err?.response?.data?.message || 'Failed to update package tool');
    }
  };

  // DataGrid Columns for Calls
  const callColumns: GridColDef[] = [
    {
      field: 'timestamp',
      headerName: 'Time',
      width: 170,
      renderCell: (params) => (
        <span className="text-xs text-gray-700 dark:text-gray-300">
          {new Date(params.value).toLocaleString()}
        </span>
      ),
    },
    {
      field: 'toolId',
      headerName: 'Tool ID',
      width: 190,
      renderCell: (params) => (
        <span className="font-mono text-xs font-semibold text-blue-600 dark:text-blue-400">
          {params.value}
        </span>
      ),
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 120,
      renderCell: (params) => (
        <Chip
          label={params.value || 'builtin'}
          size="small"
          variant="outlined"
          color={params.value === 'diagram' ? 'secondary' : params.value === 'file' ? 'info' : 'default'}
        />
      ),
    },
    {
      field: 'riskLevel',
      headerName: 'Risk',
      width: 100,
      renderCell: (params) => {
        const risk = params.value || 'read';
        return (
          <Chip
            label={risk}
            size="small"
            color={risk === 'sensitive' ? 'error' : risk === 'write' ? 'warning' : 'success'}
          />
        );
      },
    },
    {
      field: 'success',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => (
        <Chip
          icon={params.value ? <CheckCircle size={14} /> : <XCircle size={14} />}
          label={params.value ? 'Success' : 'Failed'}
          size="small"
          color={params.value ? 'success' : 'error'}
        />
      ),
    },
    {
      field: 'executionTime',
      headerName: 'Latency',
      width: 110,
      renderCell: (params) => (
        <span className="text-xs">{params.value != null ? `${params.value} ms` : '-'}</span>
      ),
    },
    {
      field: 'agentName',
      headerName: 'Agent',
      width: 150,
      renderCell: (params) => <span className="text-xs truncate">{params.value || '-'}</span>,
    },
    {
      field: 'userEmail',
      headerName: 'User',
      width: 180,
      renderCell: (params) => <span className="text-xs truncate">{params.value || '-'}</span>,
    },
    {
      field: 'requestId',
      headerName: 'Request ID',
      width: 160,
      renderCell: (params) => (
        <span className="font-mono text-xs truncate text-gray-500">{params.value || '-'}</span>
      ),
    },
    {
      field: 'actions',
      headerName: 'Details',
      width: 90,
      sortable: false,
      renderCell: (params) => (
        <IconButton size="small" onClick={() => setSelectedCall(params.row as ToolCallLogItem)}>
          <Eye size={16} />
        </IconButton>
      ),
    },
  ];

  // DataGrid Columns for Catalog
  const catalogColumns: GridColDef[] = [
    {
      field: 'toolId',
      headerName: 'Tool ID',
      width: 200,
      renderCell: (params) => <span className="font-mono text-xs font-semibold">{params.value}</span>,
    },
    {
      field: 'displayName',
      headerName: 'Display Name',
      width: 200,
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 130,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" color="primary" />
      ),
    },
    {
      field: 'riskLevel',
      headerName: 'Risk Level',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'sensitive' ? 'error' : params.value === 'write' ? 'warning' : 'success'}
        />
      ),
    },
    {
      field: 'allowedActions',
      headerName: 'Actions',
      width: 160,
      renderCell: (params) => (
        <span className="text-xs">{(params.value as string[] | undefined)?.join(', ') || 'read'}</span>
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 250,
    },
    {
      field: 'status',
      headerName: 'Global',
      width: 100,
      renderCell: (params) => (
        <Switch
          size="small"
          checked={params.row.globalEnabled ?? params.value === 'active'}
          onChange={(event) => handleToggleGlobalTool(params.row as ToolCatalogItem, event.target.checked)}
        />
      ),
    },
  ];

  const packageToolColumns: GridColDef[] = [
    {
      field: 'toolId',
      headerName: 'Tool ID',
      width: 210,
      renderCell: (params) => <span className="font-mono text-xs font-semibold">{params.value}</span>,
    },
    {
      field: 'name',
      headerName: 'Tool',
      width: 220,
    },
    {
      field: 'source',
      headerName: 'Source',
      width: 120,
      renderCell: (params) => <Chip label={params.value} size="small" variant="outlined" />,
    },
    {
      field: 'riskLevel',
      headerName: 'Risk',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'sensitive' ? 'error' : params.value === 'write' ? 'warning' : 'success'}
        />
      ),
    },
    {
      field: 'isEnabled',
      headerName: 'Package',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Switch
          size="small"
          checked={params.value}
          onChange={(event) => handleTogglePackageTool(params.row as PackageToolAssignment, event.target.checked)}
          disabled={!params.row.globalEnabled || updatePackageToolsMutation.isPending}
        />
      ),
    },
    {
      field: 'description',
      headerName: 'Description',
      flex: 1,
      minWidth: 260,
    },
  ];

  // DataGrid Columns for External Webhooks
  const extColumns: GridColDef[] = [
    {
      field: 'name',
      headerName: 'Name',
      width: 180,
      renderCell: (params) => <span className="font-semibold text-xs">{params.value}</span>,
    },
    {
      field: 'endpointUrl',
      headerName: 'Endpoint URL',
      width: 250,
      renderCell: (params) => <span className="font-mono text-xs truncate">{params.value}</span>,
    },
    {
      field: 'method',
      headerName: 'Method',
      width: 90,
      renderCell: (params) => (
        <Chip label={params.value} size="small" variant="outlined" />
      ),
    },
    {
      field: 'riskLevel',
      headerName: 'Risk',
      width: 90,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'sensitive' ? 'error' : params.value === 'write' ? 'warning' : 'success'}
        />
      ),
    },
    {
      field: 'hitlMode',
      headerName: 'HITL Mode',
      width: 140,
      renderCell: (params) => <span className="text-xs">{params.value}</span>,
    },
    {
      field: 'status',
      headerName: 'Status',
      width: 110,
      renderCell: (params) => (
        <Chip
          label={params.value}
          size="small"
          color={params.value === 'active' ? 'success' : params.value === 'suspended' ? 'error' : 'default'}
        />
      ),
    },
    {
      field: 'ownerUserEmail',
      headerName: 'Owner',
      width: 180,
      renderCell: (params) => <span className="text-xs truncate">{params.value}</span>,
    },
    {
      field: 'actions',
      headerName: 'Actions',
      width: 130,
      sortable: false,
      renderCell: (params) => (
        <Button
          size="small"
          variant="outlined"
          onClick={() => handleOpenStatusDialog(params.row as ExternalDefinitionItem)}
        >
          Change Status
        </Button>
      ),
    },
  ];

  return (
    <Box className="p-6 space-y-6">
      {/* Header */}
      <Box className="flex items-center justify-between">
        <div>
          <Typography variant="h4" className="font-bold text-gray-900 dark:text-slate-100">
            Tool Management
          </Typography>
          <Typography variant="body2" className="text-gray-500 dark:text-gray-400 mt-1">
            Manage global system tools, package tool entitlement, user tools, execution audit, and runtime policies.
          </Typography>
        </div>
        <Button
          variant="outlined"
          startIcon={<RefreshCw size={16} />}
          onClick={() => {
            if (currentTab === 0) {
              refetchCatalog();
              refetchPackageTools();
            }
            if (currentTab === 1) refetchExt();
            if (currentTab === 2) refetchCalls();
            if (currentTab === 3) refetchPolicies();
          }}
        >
          Refresh
        </Button>
      </Box>

      {/* Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
        <Tabs value={currentTab} onChange={(_, val) => setCurrentTab(val)}>
          <Tab icon={<Layers size={16} />} iconPosition="start" label="System Tools" />
          <Tab icon={<Globe size={16} />} iconPosition="start" label="User Tools" />
          <Tab icon={<Activity size={16} />} iconPosition="start" label="Execution Calls" />
          <Tab icon={<Settings size={16} />} iconPosition="start" label="Runtime Policies" />
        </Tabs>
      </Box>

      {/* TAB 0: SYSTEM TOOLS */}
      {currentTab === 0 && (
        <Box className="space-y-6">
          <Box className="space-y-3">
            <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
              Global System Tools
            </Typography>
            <Box className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700" sx={{ height: 430 }}>
              <DataGrid
                rows={catalogData.map((item) => ({ ...item, id: item.toolId }))}
                columns={catalogColumns}
                loading={isCatalogLoading || updateGlobalStatusMutation.isPending}
                disableRowSelectionOnClick
                pageSizeOptions={[20, 50]}
              />
            </Box>
          </Box>

          <Box className="space-y-3">
            <Box className="flex flex-wrap items-center justify-between gap-3">
              <Box>
                <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
                  Package Tool Entitlement
                </Typography>
                <Typography variant="body2" className="text-gray-500 dark:text-gray-400">
                  Only globally enabled tools can be assigned to packages.
                </Typography>
              </Box>
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel>Package</InputLabel>
                <Select
                  value={selectedPackageId}
                  label="Package"
                  onChange={(event) => setSelectedPackageId(event.target.value)}
                >
                  {packages.map((pkg) => (
                    <MenuItem key={pkg.publicId} value={pkg.publicId}>
                      {pkg.name} ({pkg.code})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
            <Box className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700" sx={{ height: 430 }}>
              <DataGrid
                rows={packageTools.map((item) => ({ ...item, id: item.toolId }))}
                columns={packageToolColumns}
                loading={isPackageToolsLoading || updatePackageToolsMutation.isPending}
                disableRowSelectionOnClick
                pageSizeOptions={[20, 50]}
              />
            </Box>
          </Box>
        </Box>
      )}

      {/* TAB 1: USER TOOLS */}
      {currentTab === 1 && (
        <Box className="space-y-4">
          <Box className="flex items-center gap-3 bg-white dark:bg-slate-800 p-4 rounded-lg border dark:border-slate-700">
            <TextField
              size="small"
              placeholder="Search webhook name, URL..."
              value={extSearch}
              onChange={(e) => {
                setExtSearch(e.target.value);
                setExtPage(0);
              }}
              sx={{ width: 280 }}
            />
            <Button variant="outlined" size="small" onClick={() => refetchExt()}>
              Refresh
            </Button>
          </Box>
          <Box className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700" sx={{ height: 600 }}>
            <DataGrid
              rows={extData?.items || []}
              columns={extColumns}
              getRowId={(row) => row.publicId}
              loading={isExtLoading}
              rowCount={extData?.pagination.total || 0}
              paginationMode="server"
              paginationModel={{ page: extPage, pageSize: extPageSize }}
              onPaginationModelChange={(newModel) => {
                setExtPage(newModel.page);
                setExtPageSize(newModel.pageSize);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      )}

      {/* TAB 2: EXECUTION CALLS */}
      {currentTab === 2 && (
        <Box className="space-y-6">
          {/* Stats Bar */}
          {statsData && (
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6} md={3}>
                <Card className="bg-white dark:bg-slate-800 border dark:border-slate-700">
                  <CardContent className="py-3 px-4">
                    <Typography variant="caption" className="text-gray-500 uppercase">
                      Total Tool Calls
                    </Typography>
                    <Typography variant="h5" className="font-bold text-gray-900 dark:text-white mt-1">
                      {statsData.totalCalls.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className="bg-white dark:bg-slate-800 border dark:border-slate-700">
                  <CardContent className="py-3 px-4">
                    <Typography variant="caption" className="text-gray-500 uppercase">
                      Success Rate
                    </Typography>
                    <Typography variant="h5" className="font-bold text-green-600 dark:text-green-400 mt-1">
                      {statsData.successRate}%
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className="bg-white dark:bg-slate-800 border dark:border-slate-700">
                  <CardContent className="py-3 px-4">
                    <Typography variant="caption" className="text-gray-500 uppercase">
                      Average Latency
                    </Typography>
                    <Typography variant="h5" className="font-bold text-blue-600 dark:text-blue-400 mt-1">
                      {statsData.avgExecutionTimeMs} ms
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <Card className="bg-white dark:bg-slate-800 border dark:border-slate-700">
                  <CardContent className="py-3 px-4">
                    <Typography variant="caption" className="text-gray-500 uppercase">
                      Failed Calls
                    </Typography>
                    <Typography variant="h5" className="font-bold text-red-600 dark:text-red-400 mt-1">
                      {statsData.failureCount.toLocaleString()}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>
          )}

          {/* Filter Toolbar */}
          <Box className="flex flex-wrap items-center gap-3 bg-white dark:bg-slate-800 p-4 rounded-lg border dark:border-slate-700">
            <TextField
              size="small"
              placeholder="Filter by Tool ID"
              value={toolIdFilter}
              onChange={(e) => {
                setToolIdFilter(e.target.value);
                setPage(0);
              }}
              sx={{ width: 180 }}
            />
            <FormControl size="small" sx={{ width: 140 }}>
              <InputLabel>Source</InputLabel>
              <Select
                value={sourceFilter}
                label="Source"
                onChange={(e) => {
                  setSourceFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="builtin">Builtin</MenuItem>
                <MenuItem value="file">File</MenuItem>
                <MenuItem value="diagram">Diagram</MenuItem>
                <MenuItem value="external_webhook">Webhook</MenuItem>
                <MenuItem value="marketplace">Marketplace</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 140 }}>
              <InputLabel>Risk Level</InputLabel>
              <Select
                value={riskFilter}
                label="Risk Level"
                onChange={(e) => {
                  setRiskFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="read">Read</MenuItem>
                <MenuItem value="write">Write</MenuItem>
                <MenuItem value="sensitive">Sensitive</MenuItem>
              </Select>
            </FormControl>
            <FormControl size="small" sx={{ width: 140 }}>
              <InputLabel>Status</InputLabel>
              <Select
                value={successFilter}
                label="Status"
                onChange={(e) => {
                  setSuccessFilter(e.target.value);
                  setPage(0);
                }}
              >
                <MenuItem value="">All</MenuItem>
                <MenuItem value="true">Success</MenuItem>
                <MenuItem value="false">Failed</MenuItem>
              </Select>
            </FormControl>
            <TextField
              size="small"
              placeholder="Request ID"
              value={requestIdFilter}
              onChange={(e) => {
                setRequestIdFilter(e.target.value);
                setPage(0);
              }}
              sx={{ width: 200 }}
            />
            {(toolIdFilter || sourceFilter || riskFilter || successFilter || requestIdFilter) && (
              <Button
                size="small"
                variant="text"
                onClick={() => {
                  setToolIdFilter('');
                  setSourceFilter('');
                  setRiskFilter('');
                  setSuccessFilter('');
                  setRequestIdFilter('');
                  setPage(0);
                }}
              >
                Clear
              </Button>
            )}
          </Box>

          {/* DataGrid */}
          <Box className="bg-white dark:bg-slate-800 rounded-lg border dark:border-slate-700" sx={{ height: 600 }}>
            <DataGrid
              rows={callsData?.items || []}
              columns={callColumns}
              getRowId={(row) => row.publicId}
              loading={isCallsLoading}
              rowCount={callsData?.pagination.total || 0}
              paginationMode="server"
              paginationModel={{ page, pageSize }}
              onPaginationModelChange={(newModel) => {
                setPage(newModel.page);
                setPageSize(newModel.pageSize);
              }}
              pageSizeOptions={[10, 20, 50, 100]}
              disableRowSelectionOnClick
            />
          </Box>
        </Box>
      )}

      {/* TAB 3: RUNTIME POLICIES */}
      {currentTab === 3 && (
        <Box className="space-y-6 max-w-3xl">
          <Card className="bg-white dark:bg-slate-800 border dark:border-slate-700">
            <CardContent className="p-6 space-y-4">
              <Box className="flex items-center justify-between">
                <div>
                  <Typography variant="h6" className="font-semibold text-gray-900 dark:text-slate-100">
                    HTTP DELETE Method Policy for External Webhooks
                  </Typography>
                  <Typography variant="body2" className="text-gray-500 dark:text-gray-400 mt-1">
                    Control whether end-users can define and execute external webhook tools using the DELETE method.
                  </Typography>
                </div>
                <FormControlLabel
                  control={
                    <Switch
                      checked={Boolean(policiesData?.allowDeleteMethod)}
                      onChange={(e) => handleToggleDeleteMethod(e.target.checked)}
                      color="warning"
                    />
                  }
                  label={policiesData?.allowDeleteMethod ? 'Enabled' : 'Disabled'}
                />
              </Box>

              <Alert severity={policiesData?.allowDeleteMethod ? 'warning' : 'info'}>
                {policiesData?.allowDeleteMethod
                  ? 'DELETE method is currently ALLOWED globally. End-users can register tools that delete remote resources. Ensure HITL confirmation is enforced for sensitive operations.'
                  : 'DELETE method is currently BLOCKED globally. Any existing external webhook tools configured with DELETE will be blocked at execution time.'}
              </Alert>
            </CardContent>
          </Card>
        </Box>
      )}

      {/* Detail Drawer */}
      <Drawer
        anchor="right"
        open={Boolean(selectedCall)}
        onClose={() => setSelectedCall(null)}
        PaperProps={{ sx: { width: { xs: '100%', sm: 600 }, p: 3 } }}
      >
        {selectedCall && (
          <Box className="space-y-4">
            <Box className="flex items-center justify-between border-b pb-3 dark:border-slate-700">
              <Typography variant="h6" className="font-bold">
                Tool Call Details
              </Typography>
              <IconButton onClick={() => setSelectedCall(null)} size="small">
                <X size={20} />
              </IconButton>
            </Box>

            <Box className="grid grid-cols-2 gap-2 text-sm">
              <div>
                <span className="text-gray-500">Tool:</span>{' '}
                <span className="font-mono font-semibold">{selectedCall.toolId}</span>
              </div>
              <div>
                <span className="text-gray-500">Status:</span>{' '}
                <span className={selectedCall.success ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                  {selectedCall.success ? 'Success' : 'Failed'}
                </span>
              </div>
              <div>
                <span className="text-gray-500">Latency:</span> {selectedCall.executionTime ?? '-'} ms
              </div>
              <div>
                <span className="text-gray-500">Source:</span> {selectedCall.source ?? 'builtin'}
              </div>
              <div>
                <span className="text-gray-500">Risk:</span> {selectedCall.riskLevel ?? 'read'}
              </div>
              <div>
                <span className="text-gray-500">Tokens / Cost:</span>{' '}
                {selectedCall.tokensUsed ?? 0} tokens / ${selectedCall.cost ?? 0}
              </div>
              <div className="col-span-2">
                <span className="text-gray-500">Request ID:</span>{' '}
                <span className="font-mono text-xs">{selectedCall.requestId || '-'}</span>
              </div>
              <div className="col-span-2">
                <span className="text-gray-500">User:</span> {selectedCall.userEmail || '-'}
              </div>
            </Box>

            {selectedCall.error && (
              <Box>
                <Typography variant="subtitle2" className="text-red-600 font-semibold">
                  Error Message
                </Typography>
                <pre className="mt-1 p-3 bg-red-50 dark:bg-red-950/40 text-red-700 dark:text-red-300 rounded text-xs overflow-x-auto whitespace-pre-wrap">
                  {selectedCall.error}
                </pre>
              </Box>
            )}

            <Box>
              <Typography variant="subtitle2" className="font-semibold text-gray-700 dark:text-gray-300">
                Redacted Input Payload
              </Typography>
              <pre className="mt-1 p-3 bg-gray-50 dark:bg-slate-900 border dark:border-slate-800 rounded text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(selectedCall.input, null, 2)}
              </pre>
            </Box>

            <Box>
              <Typography variant="subtitle2" className="font-semibold text-gray-700 dark:text-gray-300">
                Redacted Output Result
              </Typography>
              <pre className="mt-1 p-3 bg-gray-50 dark:bg-slate-900 border dark:border-slate-800 rounded text-xs font-mono overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(selectedCall.output, null, 2)}
              </pre>
            </Box>
          </Box>
        )}
      </Drawer>

      {/* External Definition Status Dialog */}
      <Dialog open={Boolean(statusDialogItem)} onClose={() => setStatusDialogItem(null)}>
        <DialogTitle>Update Webhook Status</DialogTitle>
        <DialogContent className="space-y-4 pt-2" sx={{ minWidth: 400 }}>
          <DialogContentText>
            Change status for webhook tool <strong>{statusDialogItem?.name}</strong>.
          </DialogContentText>
          <FormControl fullWidth size="small">
            <InputLabel>Status</InputLabel>
            <Select value={targetStatus} label="Status" onChange={(e) => setTargetStatus(e.target.value)}>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="disabled">Disabled</MenuItem>
              <MenuItem value="suspended">Suspended</MenuItem>
            </Select>
          </FormControl>
          <TextField
            fullWidth
            required
            label="Reason for Audit Trail"
            placeholder="e.g. Disabled due to endpoint security issue"
            multiline
            rows={3}
            value={statusReason}
            onChange={(e) => setStatusReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setStatusDialogItem(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            disabled={!statusReason.trim() || updateStatusMutation.isPending}
            onClick={handleConfirmStatusChange}
          >
            {updateStatusMutation.isPending ? <CircularProgress size={20} /> : 'Save Status'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Policy Change Confirmation Dialog */}
      <Dialog open={policyConfirmOpen} onClose={() => setPolicyConfirmOpen(false)}>
        <DialogTitle>Confirm Policy Modification</DialogTitle>
        <DialogContent className="space-y-4 pt-2" sx={{ minWidth: 400 }}>
          <DialogContentText>
            You are about to <strong>{pendingDeleteMethod ? 'ENABLE' : 'DISABLE'}</strong> the HTTP DELETE method for
            all external webhooks across the platform. An audit record will be logged.
          </DialogContentText>
          <TextField
            fullWidth
            required
            label="Reason for Audit Trail"
            placeholder="e.g. Approved enterprise deletion policy"
            multiline
            rows={3}
            value={policyReason}
            onChange={(e) => setPolicyReason(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPolicyConfirmOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            color={pendingDeleteMethod ? 'warning' : 'primary'}
            disabled={!policyReason.trim() || updatePolicyMutation.isPending}
            onClick={handleConfirmPolicyChange}
          >
            {updatePolicyMutation.isPending ? <CircularProgress size={20} /> : 'Confirm Policy Change'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};
